/**
 * Version Service
 * Handles automatic app updates and refresh notifications
 */

class VersionService {
  constructor() {
    this.currentVersion = null;
    this.checkInterval = null;
    this.isChecking = false;
    this.listeners = [];
    this.lastCheckTime = 0;
    this.checkIntervalMs = 30000; // Check every 30 seconds (was 5 minutes)
    this.minCheckInterval = 10000; // Minimum 10 seconds between checks (was 1 minute)
    this.focusCheckDelay = 2000; // Check 2 seconds after page focus
  }

  /**
   * Initialize the version service
   */
  init() {
    // Skip auto-refresh in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('VersionService: Skipping auto-refresh in development mode');
      // Set a default version in development to avoid undefined issues
      this.currentVersion = process.env.REACT_APP_VERSION || '1.0.0';
      return;
    }

    // Get current version from manifest or meta
    this.getCurrentVersion().then(version => {
      this.currentVersion = version;
      console.log('VersionService initialized with version:', version);
    });

    // Start periodic checking
    this.startPeriodicCheck();

    // Listen for visibility changes to check when user returns to tab
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // Check immediately when user returns to tab
        setTimeout(() => {
          this.forceCheck();
        }, this.focusCheckDelay);
      }
    });

    // Listen for online/offline events
    window.addEventListener('online', () => {
      // Check immediately when coming back online
      setTimeout(() => {
        this.forceCheck();
      }, 1000);
    });

    // Listen for page focus (when user clicks on window)
    window.addEventListener('focus', () => {
      // Check immediately when window gains focus
      setTimeout(() => {
        this.forceCheck();
      }, this.focusCheckDelay);
    });

    // Listen for route changes (React Router navigation)
    // This will be handled by the hook that uses this service
  }

  /**
   * Get main JS bundle filename from index.html for version comparison
   * This serves as a reliable fallback since JS filenames contain content hashes
   */
  async getMainJsHash() {
    const cacheBust = Date.now();
    const isDevelopment = process.env.NODE_ENV === 'development';

    try {
      // Fetch the index.html to extract the main JS filename
      const indexUrl = `${process.env.NODE_ENV === 'production' ? '/portal' : ''}/index.html?v=${cacheBust}`;
      const response = await fetch(indexUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.ok) {
        const html = await response.text();
        // Extract main.*.js filename - matches patterns like main.abc123.js
        const jsMatch = html.match(/main\.([a-f0-9]+)\.js/i);
        if (jsMatch && jsMatch[1]) {
          return jsMatch[1]; // Return just the hash portion
        }
      }
    } catch (error) {
      if (!isDevelopment) {
        console.warn('Could not fetch index.html for JS hash:', error);
      }
    }

    return null;
  }

  /**
   * Get current version from API endpoint with aggressive cache-busting
   */
  async getCurrentVersion() {
    const cacheBust = Date.now();
    const isDevelopment = process.env.NODE_ENV === 'development';

    try {
      // Try to get version from API endpoint first (most reliable)
      const apiUrl = `${process.env.NODE_ENV === 'production' ? '/portal' : ''}/api/version?v=${cacheBust}&t=${cacheBust}&cb=${Math.random()}`;
      const response = await fetch(apiUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.ok) {
        // Check if response is actually JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const versionData = await response.json();
          // Store the full version data for later use
          this.versionData = versionData;

          // Use buildHash as primary version identifier
          if (versionData.buildHash) {
            return versionData.buildHash;
          }
          if (versionData.version) {
            return versionData.version;
          }
          if (versionData.buildTime) {
            return versionData.buildTime;
          }
        } else if (!isDevelopment) {
          // Only warn in production if we get non-JSON response
          console.warn('Version API returned non-JSON response');
        }
      }
    } catch (error) {
      // Only log warnings in production, silently fail in development
      if (!isDevelopment) {
        console.warn('Could not fetch version from API:', error);
      }
    }

    // Fallback: Try to get JS hash from index.html
    const jsHash = await this.getMainJsHash();
    if (jsHash) {
      return jsHash;
    }

    try {
      // Fallback: try to get version from manifest.json with multiple cache-busting parameters
      const manifestUrl = `${process.env.NODE_ENV === 'production' ? '/portal' : ''}/manifest.json?v=${cacheBust}&t=${cacheBust}&cb=${Math.random()}`;
      const response = await fetch(manifestUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (response.ok) {
        // Check if response is actually JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const manifest = await response.json();
          if (manifest.version) {
            return manifest.version;
          }
        } else if (!isDevelopment) {
          // Only warn in production if we get non-JSON response
          console.warn('manifest.json returned non-JSON response');
        }
      }
    } catch (error) {
      // Only log warnings in production, silently fail in development
      if (!isDevelopment) {
        console.warn('Could not fetch manifest.json:', error);
      }
    }

    try {
      // Fallback: try to get version from meta.json with aggressive cache-busting
      const metaUrl = `${process.env.NODE_ENV === 'production' ? '/portal' : ''}/meta.json?v=${cacheBust}&t=${cacheBust}&cb=${Math.random()}`;
      const response = await fetch(metaUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (response.ok) {
        // Check if response is actually JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const meta = await response.json();
          if (meta.version) {
            return meta.version;
          }
          if (meta.buildHash) {
            return meta.buildHash;
          }
          if (meta.buildTime) {
            return meta.buildTime;
          }
        } else if (!isDevelopment) {
          // Only warn in production if we get non-JSON response
          console.warn('meta.json returned non-JSON response');
        }
      }
    } catch (error) {
      // Only log warnings in production, silently fail in development
      if (!isDevelopment) {
        console.warn('Could not fetch meta.json:', error);
      }
    }

    // Final fallback: use a stable version identifier
    return process.env.REACT_APP_VERSION || '1.0.0';
  }

  /**
   * Check if enough time has passed since last check
   */
  shouldCheck() {
    const now = Date.now();
    return (now - this.lastCheckTime) > this.minCheckInterval;
  }

  /**
   * Start periodic version checking
   */
  startPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(() => {
      if (this.shouldCheck()) {
        this.checkForUpdates();
      }
    }, this.checkIntervalMs);
  }

  /**
   * Stop periodic checking
   */
  stopPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Check for updates
   */
  async checkForUpdates() {
    // Skip in development mode
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    
    if (this.isChecking) {
      return;
    }

    this.isChecking = true;
    this.lastCheckTime = Date.now();

    try {
      const latestVersion = await this.getCurrentVersion();
      
      // Debug logging (only in development)
      if (process.env.NODE_ENV === 'development') {
        console.log('Version check - Current:', this.currentVersion, 'Latest:', latestVersion);
      }
      
      if (this.currentVersion && latestVersion !== this.currentVersion) {
        console.log('🔄 New version detected:', latestVersion, 'Current:', this.currentVersion);
        this.notifyListeners('update-available', {
          currentVersion: this.currentVersion,
          latestVersion: latestVersion,
          timestamp: Date.now()
        });
      } else if (!this.currentVersion) {
        // First time checking - store the version
        this.currentVersion = latestVersion;
        if (process.env.NODE_ENV === 'development') {
          console.log('VersionService: Initial version set to:', latestVersion);
        }
      } else if (this.currentVersion && latestVersion === this.currentVersion) {
        if (process.env.NODE_ENV === 'development') {
          console.log('No updates available - versions match');
        }
      }
    } catch (error) {
      console.warn('Error checking for updates:', error);
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Force check for updates
   */
  async forceCheck() {
    // Skip in development mode
    if (process.env.NODE_ENV === 'development') {
      return;
    }
    this.lastCheckTime = 0; // Reset last check time
    await this.checkForUpdates();
  }

  /**
   * Manual check for updates (works in development mode)
   */
  async manualCheck() {
    console.log('Manual version check triggered');
    // In development, just set a default version without fetching
    if (process.env.NODE_ENV === 'development') {
      if (!this.currentVersion) {
        this.currentVersion = process.env.REACT_APP_VERSION || '1.0.0';
        console.log('VersionService: Manual check set version to:', this.currentVersion);
      }
      return;
    }
    await this.checkForUpdates();
  }

  /**
   * Add event listener
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove event listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event, data) {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in version service listener:', error);
      }
    });
  }

  /**
   * Refresh the application
   */
  refresh() {
    console.log('Refreshing application...');
    window.location.reload();
  }

  /**
   * Force clear all caches and refresh the app
   */
  async forceRefresh() {
    console.log('Force refreshing app...');
    
    try {
      // Clear service worker cache
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.active) {
          // Send message to service worker to clear cache
          registration.active.postMessage({ type: 'CLEAR_CACHE' });
          
          // Wait a bit for cache to clear, then force reload
          setTimeout(() => {
            window.location.reload(true);
          }, 1000);
        } else {
          // No service worker, just reload
          window.location.reload(true);
        }
      } else {
        // No service worker support, just reload
        window.location.reload(true);
      }
    } catch (error) {
      console.error('Error during force refresh:', error);
      // Fallback: just reload
      window.location.reload(true);
    }
  }

  /**
   * Clear browser cache using various methods
   */
  async clearBrowserCache() {
    console.log('Clearing browser cache...');
    
    try {
      // Clear service worker cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('Service worker caches cleared');
      }
      
      // Clear localStorage and sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      console.log('Local storage cleared');
      
      // Clear IndexedDB if it exists
      if ('indexedDB' in window) {
        // Note: This is a simplified approach. In production, you might want to be more specific
        console.log('IndexedDB exists but not cleared (requires specific implementation)');
      }
      
    } catch (error) {
      console.error('Error clearing browser cache:', error);
    }
  }

  /**
   * Get the build ID for cache busting
   */
  getBuildId() {
    return this.versionData?.buildHash || this.currentVersion || 'unknown';
  }

  /**
   * Get full version info for display
   */
  getVersionInfo() {
    return {
      version: this.versionData?.version || '1.0.0',
      buildHash: this.getBuildId(),
      buildTime: this.versionData?.buildTime,
      environment: this.versionData?.environment || process.env.NODE_ENV || 'development'
    };
  }

  /**
   * Get version data for debugging
   */
  getVersionData() {
    return this.versionData || null;
  }

  /**
   * Cleanup
   */
  destroy() {
    this.stopPeriodicCheck();
    this.listeners = [];
  }
}

// Create singleton instance
const versionService = new VersionService();

export default versionService;
